/**
 * IndexedDB storage for offline POS (product catalog + pending sales queue).
 * Additive layer — online API paths in posService are unchanged.
 */
import { POSPaymentMethod, POSProduct, POSTransaction } from '../types/pos';

const DB_NAME = 'stockm_pos_offline';
const DB_VERSION = 1;

export type OfflineSaleStatus = 'pending' | 'synced' | 'failed';

export type OfflineQueuedSale = {
  local_id: string;
  created_at: string;
  location_id: number;
  served_by?: number;
  items: Array<{
    product_id: number;
    product_code?: string;
    product_name: string;
    barcode?: string;
    quantity: number;
    unit_price: number;
    total_price: number;
  }>;
  payment_method: string;
  payment_reference?: string | null;
  payer_phone?: string | null;
  receipt_printed?: boolean;
  tax_amount: number;
  total_amount: number;
  buyer_name?: string | null;
  buyer_cid?: string | null;
  buyer_tpn?: string | null;
  buyer_address?: string | null;
  status: OfflineSaleStatus;
  sync_error?: string;
  server_transaction_id?: number;
  server_transaction_number?: string;
};

export type OfflineStashedCart = {
  id: string;
  serial: string;
  time: string;
  items: Array<{
    id: string;
    product_id: number;
    product_code?: string;
    product_name: string;
    barcode?: string;
    quantity: number;
    unit_price: number;
    sale_unit_price: number;
    total_price: number;
    stock_quantity?: number;
  }>;
  total_amount: number;
  created_at: string;
  location_id: number;
};

type CatalogMeta = {
  location_id: number;
  synced_at: string;
  product_count: number;
};

type CachedProductRow = POSProduct & { location_id: number; key: string };

function openOfflineDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('Failed to open offline DB'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('products')) {
        const store = db.createObjectStore('products', { keyPath: 'key' });
        store.createIndex('location_id', 'location_id', { unique: false });
      }
      if (!db.objectStoreNames.contains('meta')) {
        db.createObjectStore('meta', { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains('offline_sales')) {
        const sales = db.createObjectStore('offline_sales', { keyPath: 'local_id' });
        sales.createIndex('status', 'status', { unique: false });
      }
      if (!db.objectStoreNames.contains('offline_stashes')) {
        const stashes = db.createObjectStore('offline_stashes', { keyPath: 'id' });
        stashes.createIndex('location_id', 'location_id', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

function productKey(locationId: number, productId: number): string {
  return `${locationId}:${productId}`;
}

function txDone<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

export function isOfflineStorageAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

export async function saveProductCatalog(locationId: number, products: POSProduct[]): Promise<void> {
  if (!isOfflineStorageAvailable()) return;
  const db = await openOfflineDb();
  const tx = db.transaction(['products', 'meta'], 'readwrite');
  const productStore = tx.objectStore('products');
  const index = productStore.index('location_id');
  const existingKeys = await txDone(index.getAllKeys(locationId));
  for (const key of existingKeys) {
    productStore.delete(key);
  }
  for (const p of products) {
    productStore.put({
      key: productKey(locationId, p.id),
      location_id: locationId,
      ...p,
    } satisfies CachedProductRow);
  }
  const meta: CatalogMeta = {
    location_id: locationId,
    synced_at: new Date().toISOString(),
    product_count: products.length,
  };
  tx.objectStore('meta').put({ key: `catalog:${locationId}`, ...meta });
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function getCatalogMeta(locationId: number): Promise<CatalogMeta | null> {
  if (!isOfflineStorageAvailable()) return null;
  const db = await openOfflineDb();
  const meta = await txDone(
    db.transaction('meta', 'readonly').objectStore('meta').get(`catalog:${locationId}`),
  );
  db.close();
  if (!meta) return null;
  return {
    location_id: meta.location_id,
    synced_at: meta.synced_at,
    product_count: meta.product_count,
  };
}

async function getAllCachedProducts(locationId: number): Promise<POSProduct[]> {
  if (!isOfflineStorageAvailable()) return [];
  const db = await openOfflineDb();
  const rows = await txDone(
    db.transaction('products', 'readonly').objectStore('products').index('location_id').getAll(
      locationId,
    ),
  );
  db.close();
  return (rows as CachedProductRow[]).map(({ location_id: _loc, key: _key, ...p }) => p);
}

export async function searchCachedProducts(
  locationId: number,
  search?: string,
  category?: string,
): Promise<POSProduct[]> {
  const all = await getAllCachedProducts(locationId);
  let list = all;
  const term = (search || '').trim().toLowerCase();
  if (term) {
    list = list.filter(
      (p) =>
        p.name.toLowerCase().includes(term) ||
        (p.code || '').toLowerCase().includes(term) ||
        (p.barcode || '').toLowerCase().includes(term),
    );
  }
  if (category && category !== 'All') {
    list = list.filter((p) => (p.category || 'Other') === category);
  }
  return list;
}

export async function findCachedProductByBarcode(
  locationId: number,
  raw: string,
): Promise<POSProduct | null> {
  const code = raw.trim().toLowerCase();
  if (!code) return null;
  const all = await getAllCachedProducts(locationId);
  return (
    all.find(
      (p) =>
        (p.barcode || '').trim().toLowerCase() === code ||
        (p.code || '').trim().toLowerCase() === code,
    ) ?? null
  );
}

export async function adjustCachedStock(
  locationId: number,
  productId: number,
  delta: number,
): Promise<void> {
  if (!isOfflineStorageAvailable()) return;
  const db = await openOfflineDb();
  const tx = db.transaction('products', 'readwrite');
  const store = tx.objectStore('products');
  const row = (await txDone(store.get(productKey(locationId, productId)))) as CachedProductRow | undefined;
  if (!row) {
    db.close();
    return;
  }
  const next = Math.max(0, (row.stock_quantity ?? 0) + delta);
  store.put({ ...row, stock_quantity: next });
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function enqueueOfflineSale(sale: OfflineQueuedSale): Promise<void> {
  if (!isOfflineStorageAvailable()) {
    throw new Error('Offline storage is not available in this browser');
  }
  const db = await openOfflineDb();
  const tx = db.transaction('offline_sales', 'readwrite');
  tx.objectStore('offline_sales').put(sale);
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function listOfflineSales(status?: OfflineSaleStatus): Promise<OfflineQueuedSale[]> {
  if (!isOfflineStorageAvailable()) return [];
  const db = await openOfflineDb();
  const store = db.transaction('offline_sales', 'readonly').objectStore('offline_sales');
  const all = (await txDone(store.getAll())) as OfflineQueuedSale[];
  db.close();
  if (!status) return all;
  return all.filter((s) => s.status === status);
}

export async function countPendingOfflineSales(): Promise<number> {
  const pending = await listOfflineSales('pending');
  return pending.length;
}

export async function updateOfflineSale(
  localId: string,
  patch: Partial<OfflineQueuedSale>,
): Promise<void> {
  if (!isOfflineStorageAvailable()) return;
  const db = await openOfflineDb();
  const tx = db.transaction('offline_sales', 'readwrite');
  const store = tx.objectStore('offline_sales');
  const existing = (await txDone(store.get(localId))) as OfflineQueuedSale | undefined;
  if (!existing) {
    db.close();
    return;
  }
  store.put({ ...existing, ...patch, local_id: localId });
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function saveOfflineStash(stash: OfflineStashedCart): Promise<void> {
  if (!isOfflineStorageAvailable()) {
    throw new Error('Offline storage is not available in this browser');
  }
  const db = await openOfflineDb();
  const tx = db.transaction('offline_stashes', 'readwrite');
  tx.objectStore('offline_stashes').put(stash);
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function listOfflineStashes(locationId?: number): Promise<OfflineStashedCart[]> {
  if (!isOfflineStorageAvailable()) return [];
  const db = await openOfflineDb();
  const store = db.transaction('offline_stashes', 'readonly').objectStore('offline_stashes');
  const all = (await txDone(store.getAll())) as OfflineStashedCart[];
  db.close();
  const filtered = locationId != null ? all.filter((s) => s.location_id === locationId) : all;
  return filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
}

export async function deleteOfflineStash(id: string): Promise<void> {
  if (!isOfflineStorageAvailable()) return;
  const db = await openOfflineDb();
  const tx = db.transaction('offline_stashes', 'readwrite');
  tx.objectStore('offline_stashes').delete(id);
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export function createOfflineStash(
  locationId: number,
  items: OfflineStashedCart['items'],
): OfflineStashedCart {
  const createdAt = new Date().toISOString();
  const localId = createOfflineLocalId();
  const totalAmount = items.reduce((sum, item) => sum + Number(item.total_price || 0), 0);
  const serial = `STASH-${createdAt.slice(0, 10).replace(/-/g, '')}-${localId.replace(/-/g, '').slice(0, 6).toUpperCase()}`;
  return {
    id: `offline-stash-${localId}`,
    serial,
    time: new Date(createdAt).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }),
    items,
    total_amount: totalAmount,
    created_at: createdAt,
    location_id: locationId,
  };
}

export function createOfflineLocalId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `offline-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function buildOfflineTransactionNumber(localId: string, createdAt: string): string {
  const d = createdAt.slice(0, 10).replace(/-/g, '');
  const short = localId.replace(/-/g, '').slice(0, 8).toUpperCase();
  return `OFFLINE-${d}-${short}`;
}

export function offlineSaleToReceiptTransaction(
  sale: OfflineQueuedSale,
  cashierName?: string,
): POSTransaction {
  const txnNumber = buildOfflineTransactionNumber(sale.local_id, sale.created_at);
  const created = new Date(sale.created_at);
  return {
    id: 0,
    transaction_number: txnNumber,
    serial: txnNumber,
    location_id: sale.location_id,
    sale_date: created.toISOString().slice(0, 10),
    sale_time: created.toLocaleTimeString('en-US', {
      hour12: true,
      hour: '2-digit',
      minute: '2-digit',
    }),
    payment_method: sale.payment_method as POSPaymentMethod,
    payment_reference: sale.payment_reference || undefined,
    payer_phone: sale.payer_phone || undefined,
    receipt_printed: sale.receipt_printed !== false,
    total_amount: sale.total_amount,
    tax_amount: sale.tax_amount,
    discount_amount: 0,
    coupon_discount: 0,
    served_by: sale.served_by ?? 0,
    cashier_name: cashierName,
    created_at: sale.created_at,
    status: 'COMPLETED',
    buyer_name: sale.buyer_name || null,
    buyer_cid: sale.buyer_cid || null,
    buyer_tpn: sale.buyer_tpn || null,
    buyer_address: sale.buyer_address || null,
    items: sale.items.map((item, idx) => ({
      id: `offline-${idx}`,
      product_id: item.product_id,
      product_code: item.product_code,
      product_name: item.product_name,
      barcode: item.barcode,
      quantity: item.quantity,
      unit_price: item.unit_price,
      sale_unit_price: item.unit_price,
      total_price: item.total_price,
    })),
  };
}
